import { isAccessDeniedError } from '@/api';
import { useUser } from '@/app/providers';
import { hasFeature } from '@/domain/authentication';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useDeviceRestrictions, useDeviceUserLevelKeys } from '../hooks';
import { DeviceTabAccessDenied, DeviceTabError, DeviceTabLoading } from './DeviceTabStates';
import { TheftProtectionCard } from './rules/TheftProtectionCard';
import { UserLevelKeysCard } from './rules/UserLevelKeysCard';
import { WorkScheduleCard } from './rules/WorkScheduleCard';

// DeviceRulesTab shows the device restrictions: theft protection, work schedule with overrule
// code and, behind the superuser.userLevelKeys flag, the user level keys. The restrictions
// endpoint needs user level 1, so a 403 here is independent of the other tabs.
export function DeviceRulesTab() {
  const { t } = useTranslation();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const { user } = useUser();
  const canSeeUserLevelKeys = Boolean(user && hasFeature(user, 'superuser.userLevelKeys'));

  // Both endpoints are independent, so the keys request starts next to the restrictions one
  // instead of waiting for the card below to mount.
  const restrictionsQuery = useDeviceRestrictions(deviceId);
  const keysQuery = useDeviceUserLevelKeys(deviceId, canSeeUserLevelKeys);

  if (restrictionsQuery.isLoading) {
    return <DeviceTabLoading />;
  }

  if (restrictionsQuery.isError || !restrictionsQuery.data) {
    if (isAccessDeniedError(restrictionsQuery.error)) {
      return (
        <DeviceTabAccessDenied
          title={t('devices.detail.rules.accessDenied.title')}
          description={t('devices.detail.rules.accessDenied.description')}
        />
      );
    }
    return <DeviceTabError title={t('devices.detail.rules.error')} />;
  }

  const { theftProtection, workSchedule, overrideKey } = restrictionsQuery.data;

  return (
    <div className="flex w-full flex-col gap-4 overflow-scroll">
      <TheftProtectionCard theftProtection={theftProtection} />
      <WorkScheduleCard workSchedule={workSchedule} overrideKey={overrideKey} />

      {canSeeUserLevelKeys && (
        <UserLevelKeysCard
          deviceId={deviceId}
          keys={keysQuery.data ?? []}
          isLoading={keysQuery.isLoading}
          error={keysQuery.error}
        />
      )}
    </div>
  );
}
